import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchor,
  ExperienceAnchorType,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  CustomUserField,
  toCustomFieldKey
} from '@dadohr/shared/domainTypes/org';
import { CALCULATED_FIELDS, IUser } from '@dadohr/shared/domainTypes/user';
import { capitalize, compact } from 'lodash';
import { useCallback, useMemo } from 'react';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { useCurrentUser } from '../../../../services/currentUser';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { addToTs, Timestamp, toMoment } from '../../../../services/time';
import { getUserValue, useUserById } from '../../../../services/user';

export type AnchorField = {
  label: (t: UnsafeTFunction) => string;
  shortLabel: (t: UnsafeTFunction) => string;
  field: string;
  key: string;
  isRecurring: boolean;
};

const ANCHOR_FIELDS: AnchorField[] = [
  {
    label: (t) => t(`participant's first day at work`),
    shortLabel: (t) => t('first day at work'),
    field: 'hireDate',
    key: 'startDate',
    isRecurring: false
  },
  {
    label: (t) => t(`participant's last day at work`),
    shortLabel: (t) => t(`last day at work`),
    field: 'terminationDate',
    key: 'terminationDate',
    isRecurring: false
  },
  ...compact(
    CALCULATED_FIELDS.map((c) => {
      if (c.type === 'recurringDate') {
        return {
          label: (t: UnsafeTFunction) =>
            `${t(`participant's`)} ${c.label.toLowerCase()}`,
          shortLabel: () => c.label.toLowerCase(),
          field: `calculatedFields.recurringDate.${c.field}.current.date`,
          key: `calculatedFields.${c.key}.date`,
          isRecurring: true
        };
      }
      return null;
    })
  ),
  {
    label: (t) => t(`experience start`),
    shortLabel: (t) => t(`start`),
    field: 'experienceStart',
    key: 'experienceStart',
    isRecurring: false
  },
  {
    label: (t) => t(`experience end`),
    shortLabel: (t) => t(`end`),
    field: 'experienceEnd',
    key: 'experienceEnd',
    isRecurring: false
  }
];

export const getAnchorFields = (
  customFields: CustomUserField[]
): AnchorField[] => {
  return [
    ...ANCHOR_FIELDS,
    ...customFields
      .filter((f) => f.type === 'date' && !f.restricted && !f.hideYear)
      .map((f) => {
        const key = toCustomFieldKey(f.key);
        return {
          field: key,
          key,
          label: (t: UnsafeTFunction) =>
            `${t(`participant's`)} ${f.label.toLowerCase()}`,
          shortLabel: () => f.label.toLowerCase(),
          isRecurring: false
        };
      })
  ];
};

export const useAnchorFields = (
  customFields: CustomUserField[],
  opts: { realDatesOnly?: boolean } = {}
): AnchorField[] => {
  const { realDatesOnly } = opts;
  return useMemo(() => {
    let fields = getAnchorFields(customFields);
    if (realDatesOnly) {
      fields = fields.filter(
        (x) => x.key !== 'experienceStart' && x.key !== 'experienceEnd'
      );
    }

    return fields;
  }, [customFields, realDatesOnly]);
};

/** Short label of a milestone experience's anchor field, or null for other anchor types. */
export const useMilestoneShortLabel = (
  anchor: ExperienceAnchor
): string | null => {
  const { org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  return useMemo(() => {
    if (anchor.type !== ExperienceAnchorType.EMPLOYEE_DATE) return null;
    const field = getAnchorFields(org.customUserFields).find(
      (f) => f.field === anchor.field
    );
    return field?.shortLabel(t) || anchor.field;
  }, [anchor, org.customUserFields, t]);
};

export const useTemplateAnchorField = (
  tpl: Doc<IExperienceTemplate>,
  customFields: CustomUserField[]
) => {
  const anchorFields = useAnchorFields(customFields);
  const anchor = tpl.data.anchor;
  return useMemo(() => {
    if (anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
      return anchorFields.find((a) => a.field === anchor.field) || null;
    }
    return null;
  }, [anchorFields, anchor]);
};

/**
 * @deprecated see getAbsoluteAnchorDefinition and getRelativeAnchorDefinition
 */
export const getAnchorDefinitionOld = (
  anchor: ExperienceAnchor,
  anchorOffsetOverride: number | null,
  anchorFields: AnchorField[],
  t: UnsafeTFunction
) => {
  if (
    anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
    anchor.offset.type === 'ABSOLUTE' &&
    anchor.offset.duration < 0
  ) {
    const field = anchorFields.find((a) => a.field === anchor.field);
    return {
      position: Math.abs(
        anchorOffsetOverride === null
          ? anchor.offset.duration
          : anchorOffsetOverride
      ),
      originalOffset: anchor.offset.duration,
      label: capitalize(field?.label(t) || anchor.field),
      shortLabel: capitalize(field?.shortLabel(t) || anchor.field)
    };
  }
  return null;
};

/**
 * @deprecated see useAbsoluteAnchorRulers and useRelativeAnchorRulers
 */
export const useAnchorRulersOld = (
  anchor: ExperienceAnchor,
  anchorOffsetOverride: number | null,
  anchorFields: AnchorField[],
  t: UnsafeTFunction
) => {
  const ruler = useMemo(
    () => getAnchorDefinitionOld(anchor, anchorOffsetOverride, anchorFields, t),
    [anchor, anchorOffsetOverride, anchorFields, t]
  );
  return useMemo(() => (ruler ? [ruler] : undefined), [ruler]);
};

export const getAbsoluteAnchorDefinition = (
  experienceStart: Timestamp,
  anchor: ExperienceAnchor,
  anchorTiming: {
    at: Timestamp;
  },
  anchorFields: AnchorField[],
  t: UnsafeTFunction
) => {
  if (
    anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
    anchor.offset.type === 'ABSOLUTE' &&
    anchor.offset.duration < 0
  ) {
    const field = anchorFields.find((a) => a.field === anchor.field);
    return {
      position: anchorTiming.at.toMillis() - experienceStart.toMillis(),
      anchorTiming,
      label: capitalize(field?.label(t) || anchor.field),
      shortLabel: capitalize(field?.shortLabel(t) || anchor.field)
    };
  }
  return null;
};

export const useAbsoluteAnchorRulers = (
  experienceStart: Timestamp,
  anchor: ExperienceAnchor,
  anchorTiming: {
    at: Timestamp;
  },
  anchorFields: AnchorField[],
  t: UnsafeTFunction
) => {
  const ruler = useMemo(
    () =>
      getAbsoluteAnchorDefinition(
        experienceStart,
        anchor,
        anchorTiming,
        anchorFields,
        t
      ),
    [experienceStart, anchor, anchorTiming, anchorFields, t]
  );
  return useMemo(() => (ruler ? [ruler] : undefined), [ruler]);
};

const STATIC_FLAG_CONFIG = {
  experienceStart: {
    colorMode: 'light',
    alignLabel: 'right'
  },
  experienceEnd: {
    colorMode: 'light',
    alignLabel: 'left'
  }
};

export const useAllAbsoluteAnchorRulersWithDates = (
  experienceStart: Timestamp,
  anchor: ExperienceAnchor,
  anchorTiming: {
    at: Timestamp;
  },
  anchorFields: AnchorField[],
  t: UnsafeTFunction,
  expectedEnd: number,
  showExpectedEndFlag: boolean
): {
  position: number;
  anchorTiming: {
    at: Timestamp;
  };
  label: string;
  shortLabel: string;
  static: boolean;
  colorMode?: 'light' | 'dark';
  alignLabel?: 'left' | 'center' | 'right';
}[] => {
  const absoluteAnchors = useAbsoluteAnchorRulers(
    experienceStart,
    anchor,
    anchorTiming,
    anchorFields,
    t
  );
  return useMemo(() => {
    const expStart = anchorFields.find((a) => a.key === 'experienceStart');
    const experienceEnd = anchorFields.find((a) => a.key === 'experienceEnd');
    const absoluteAnchorsWithDates = absoluteAnchors?.map((a) => {
      return {
        ...a,
        label: `${a.label} (${toMoment(a.anchorTiming.at).format(
          'ddd, MM/DD/YYYY'
        )})`,
        static: false
      };
    });
    return compact([
      {
        position: 0,
        anchorTiming,
        label:
          `${capitalize(expStart?.label(t))} (${toMoment(
            experienceStart
          ).format('ddd, MM/DD/YYYY')})` || capitalize('experience start'),
        shortLabel: capitalize(expStart?.label(t) || 'experience start'),
        ...STATIC_FLAG_CONFIG.experienceStart,
        static: true
      },
      ...(absoluteAnchorsWithDates || []),
      expectedEnd &&
        showExpectedEndFlag && {
          position: expectedEnd,
          anchorTiming,
          label:
            `${capitalize(experienceEnd?.label(t))} (${toMoment(
              addToTs(experienceStart, expectedEnd)
            ).format('ddd, MM/DD/YYYY')})` || capitalize('experience end'),
          shortLabel: capitalize(experienceEnd?.label(t) || 'experience end'),
          static: true,
          ...STATIC_FLAG_CONFIG.experienceEnd
        }
    ]);
  }, [
    absoluteAnchors,
    expectedEnd,
    showExpectedEndFlag,
    experienceStart,
    anchorFields,
    anchorTiming,
    t
  ]);
};

export const getRelativeAnchorDefinition = (
  anchor: ExperienceAnchor,
  anchorFields: AnchorField[],
  t: UnsafeTFunction
) => {
  if (
    anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
    anchor.offset.type === 'ABSOLUTE' &&
    anchor.offset.duration < 0
  ) {
    const field = anchorFields.find((a) => a.field === anchor.field);
    return {
      position: anchor.offset.duration * -1,
      label: capitalize(field?.label(t) || anchor.field),
      shortLabel: capitalize(field?.shortLabel(t) || anchor.field)
    };
  }
  return null;
};

export const useRelativeAnchorRulers = (
  anchor: ExperienceAnchor,
  anchorFields: AnchorField[],
  t: UnsafeTFunction
) => {
  const ruler = useMemo(
    () => getRelativeAnchorDefinition(anchor, anchorFields, t),
    [anchor, anchorFields, t]
  );
  return useMemo(() => (ruler ? [ruler] : undefined), [ruler]);
};

export const useAllRelevantAnchorRulers = (
  anchor: ExperienceAnchor,
  anchorFields: AnchorField[],
  t: UnsafeTFunction,
  expectedEnd: number,
  showExpectedEndFlag: boolean
): {
  position: number;
  label: string;
  shortLabel: string;
  colorMode?: 'light' | 'dark';
  alignLabel?: 'left' | 'center' | 'right';
}[] => {
  const relativeRulers = useRelativeAnchorRulers(anchor, anchorFields, t);
  return useMemo(() => {
    const experienceStart = anchorFields.find(
      (a) => a.key === 'experienceStart'
    );
    const experienceEnd = anchorFields.find((a) => a.key === 'experienceEnd');
    return compact([
      {
        position: 0,
        label: capitalize(experienceStart?.label(t) || 'experience start'),
        shortLabel: capitalize(experienceStart?.shortLabel(t) || 'start'),
        ...STATIC_FLAG_CONFIG.experienceStart
      },
      ...(relativeRulers || []),
      expectedEnd &&
        showExpectedEndFlag && {
          position: expectedEnd,
          label: capitalize(experienceEnd?.label(t) || 'experience end'),
          shortLabel: capitalize(
            experienceEnd?.shortLabel(t) || 'experience end'
          ),
          ...STATIC_FLAG_CONFIG.experienceEnd
        }
    ]);
  }, [relativeRulers, expectedEnd, showExpectedEndFlag, anchorFields, t]);
};

export const useAnchorWarning = (
  participantId: string,
  anchor: ExperienceAnchor,
  anchorTiming: {
    at: Timestamp;
  },
  tz: string
) => {
  const currentDate = anchorTiming.at;
  const m = useCallback(
    (user: Doc<IUser> | null) => {
      if (!user) {
        return null;
      }
      if (anchor.type !== ExperienceAnchorType.EMPLOYEE_DATE) {
        return;
      }
      const origDate = getUserValue(user.data, anchor.field, currentDate);
      const isSameDay = toMoment(origDate)
        .tz(tz)
        .isSame(toMoment(currentDate).tz(tz), 'day');
      if (isSameDay) {
        return null;
      }

      return {
        original: origDate,
        current: currentDate
      };
    },
    [anchor, currentDate, tz]
  );

  return useMappedLoadingValue(useUserById(participantId), m, true);
};
